import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertyApi, favoriteApi, Property } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * 분양 현장 상세 정보 관리 Hook
 * - ID 또는 house_manage_no(주택관리번호) 모두 지원
 * - 예: usePropertyDetail('44') 또는 usePropertyDetail('2021000959')
 */
export const usePropertyDetail = (propertyId: string | undefined) => {
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  // v2 S3: 404 가 아닌 로드 실패(네트워크·5xx)를 구분 — 오류코드(E-503/E-NET) 표기용 (2026-09-21)
  const [loadError, setLoadError] = useState<{ code: string; at: Date } | null>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // 늦게 도착한 이전 요청 응답은 버린다 — A→B 빠른 이동 시 A 데이터가 B 를 덮지 않게 (Codex 검토 2026-10-05)
  const seqRef = useRef(0);
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const prevAuthRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (propertyId) {
      // 같은 현장의 주소만 바뀐 경우(숫자 ID → 주택관리번호 canonical 이동)는 이미 받은 데이터를 그대로 쓴다
      if (property && (String(property.id) === propertyId || property.house_manage_no === propertyId)) return;
      setProperty(null);          // 다른 현장으로 바뀌면 이전 현장 화면·주소가 남지 않게
      loadPropertyDetail(propertyId);
      trackRecentView(propertyId);
    }
  }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 로그인·로그아웃이 바뀌면 조용히 다시 받는다 — 로그아웃 뒤 회원 전용 혜택이 화면에 남지 않게 (2026-10-05)
  useEffect(() => {
    if (authLoading) return;
    if (prevAuthRef.current === null) {
      prevAuthRef.current = isAuthenticated;
      return;
    }
    if (prevAuthRef.current !== isAuthenticated) {
      prevAuthRef.current = isAuthenticated;
      if (propertyId) loadPropertyDetail(propertyId, { silent: true });
    }
  }, [isAuthenticated, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPropertyDetail = async (id: string, opts: { silent?: boolean } = {}) => {
    const seq = ++seqRef.current;
    try {
      if (!opts.silent) setLoading(true);
      setLoadError(null);
      const data = await propertyApi.getDetail(id);
      if (seq !== seqRef.current) return;   // 더 최근 요청이 있다 — 이 응답은 버림
      setProperty(data);
      setIsFavorited(data.is_favorited || false);
    } catch (error) {
      if (seq !== seqRef.current) return;
      console.error('Failed to load property detail:', error);
      const status = (error as any)?.response?.status;
      if (status !== 404) {
        setLoadError({ code: typeof status === 'number' ? `E-${status}` : 'E-NET', at: new Date() });
      }
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  };

  const trackRecentView = async (id: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await propertyApi.getDetail(id);
      }
    } catch (error) {
      console.error('Failed to track recent view:', error);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!property) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('로그인이 필요한 서비스입니다.');
      navigate('/login');
      return;
    }

    try {
      setFavoriteLoading(true);
      await favoriteApi.toggle(property.id);
      setIsFavorited(!isFavorited);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      alert('찜하기 처리 중 오류가 발생했습니다.');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const reload = () => {
    if (propertyId) loadPropertyDetail(propertyId);
  };
  /** 화면을 비우지 않고 다시 받기 — 혜택 안내 요청 직후 등 (2026-10-08) */
  const refresh = () => {
    if (propertyId) loadPropertyDetail(propertyId, { silent: true });
  };

  return {
    property,
    loading,
    loadError,
    reload,
    refresh,
    isFavorited,
    favoriteLoading,
    handleFavoriteToggle,
  };
};
